import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RootState } from "../store/store";

export interface KAgentQuery {
  query: string;
  facility_id?: string;
}

export interface ChartVisualization {
  type: "bar" | "line" | "pie";
  title: string;
  image: string; // Base64 PNG
  timestamp?: string;
  raw_data?: {
    labels: string[];
    values: number[];
  };
}

export interface KAgentResponse {
  response: string;
  facility_id: string;
  data?: Record<string, unknown>;
  visualization?: ChartVisualization;
  timestamp?: string;
}

export interface UserFacilities {
  username: string;
  organization: string;
  facilities: string[];
}


export interface Message {
  id: string;
  content: string;
  role: "user" | "assistant";
  timestamp: string;
  facility_id?: string;
  visualization?: ChartVisualization;
  metadata?: Record<string, unknown>;
}

export interface Conversation {
  id: string;
  user_id?: string;
  facility_id?: string;
  title: string;
  messages: Message[];
  created_at: string;
  updated_at: string;
}

export interface CreateConversationRequest {
  facility_id?: string;
  title?: string;
}

export interface AddMessageRequest {
  role: "user" | "assistant";
  content: string;
  metadata?: Record<string, unknown>;
}

export interface UserPreferences {
  preferred_units?: string;
  preferred_timezone?: string;
  default_facility?: string;
  email_notifications?: boolean;
  saved_queries?: Array<{ query: string; name?: string; timestamp?: string }>;
}

// Using environment variable or fallback to localhost for development
const K_AGENT_BASE_URL = import.meta.env.VITE_K_AGENT_API || "http://localhost:8000";

export const kAgentApi = createApi({
  reducerPath: "kAgentApi",
  baseQuery: fetchBaseQuery({
    baseUrl: K_AGENT_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Conversation", "UserFacilities"],
  endpoints: (builder) => ({
    // Health check endpoint
    healthCheck: builder.query<{ status: string; version?: string }, void>({
      query: () => "/",
    }),
    
    // Get user's accessible facilities
    getUserFacilities: builder.query<UserFacilities, void>({
      query: () => "/api/user/facilities",
      providesTags: ["UserFacilities"],
    }),

    // Send a query to K-Agent
    sendQuery: builder.mutation<KAgentResponse, KAgentQuery>({
      query: (body) => ({
        url: "/api/query",
        method: "POST",
        body,
      }),
    }),

    // === CONVERSATION ENDPOINTS ===
    
    // Create new conversation
    createConversation: builder.mutation<{ conversation_id: string }, CreateConversationRequest>({
      query: (body) => ({
        url: "/api/conversations",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Conversation"],
    }),

    // List conversations
    listConversations: builder.query<Conversation[], { facility_id?: string; limit?: number; offset?: number }>({
      query: (params) => ({
        url: "/api/conversations",
        params,
      }),
      providesTags: ["Conversation"],
    }),

    // Get specific conversation
    getConversation: builder.query<Conversation, string>({
      query: (id) => `/api/conversations/${id}`,
      providesTags: (result, error, id) => [{ type: "Conversation", id }],
    }),

    // Add message to conversation
    addMessage: builder.mutation<Message, { conversationId: string; message: AddMessageRequest }>({
      query: ({ conversationId, message }) => ({
        url: `/api/conversations/${conversationId}/messages`,
        method: "POST",
        body: message,
      }),
      invalidatesTags: (result, error, { conversationId }) => [
        { type: "Conversation", id: conversationId },
      ],
    }),

    // Update conversation (e.g., change title)
    updateConversation: builder.mutation<Conversation, { id: string; title?: string; metadata?: any }>({
      query: ({ id, ...body }) => ({
        url: `/api/conversations/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Conversation", id },
        "Conversation",
      ],
    }),

    // Delete conversation
    deleteConversation: builder.mutation<{ status: string }, string>({
      query: (id) => ({
        url: `/api/conversations/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Conversation"],
    }),

    // Search conversations
    searchConversations: builder.query<Conversation[], { q: string; facility_id?: string }>({
      query: (params) => ({
        url: "/api/conversations/search",
        params,
      }),
    }),

    // === USER PREFERENCES ===
    
    // Get user preferences
    getUserPreferences: builder.query<UserPreferences, void>({
      query: () => "/api/user/preferences",
    }),

    // Update user preferences
    updateUserPreferences: builder.mutation<UserPreferences, UserPreferences>({
      query: (body) => ({
        url: "/api/user/preferences",
        method: "PUT",
        body,
      }),
    }),

    // === OTHER ENDPOINTS ===
    
    // Get active alarms
    getActiveAlarms: builder.query<Array<{ id: string; message: string; severity: string; timestamp: string }>, { facility_id?: string }>({
      query: (params) => ({
        url: "/api/alarms",
        params,
      }),
    }),

    // Export data
    exportData: builder.mutation<{ url?: string; status: string; message?: string }, { format: string; query?: string; email?: string }>({
      query: (body) => ({
        url: "/api/export",
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useHealthCheckQuery,
  useGetUserFacilitiesQuery,
  useSendQueryMutation,
  useGetActiveAlarmsQuery,
  useExportDataMutation,
  useCreateConversationMutation,
  useListConversationsQuery,
  useGetConversationQuery,
  useAddMessageMutation,
  useUpdateConversationMutation,
  useDeleteConversationMutation,
  useSearchConversationsQuery,
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
} = kAgentApi;